// src/components/Footer.jsx
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FiInstagram, FiMail, FiPhone, FiMapPin } from "react-icons/fi";

import { useLang } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

export const CONTACT = {
  company: "Kaida Beauty Studio SH.P.K.",
  address: "Rr. Marin Barleti nr. 42, 12060 Bardh i Madh, Kosovë",
  phone: "+383 49 123 123",
  phoneHref: "+38349123123",
  email: "kaidabeautystudio@gmail.com",
  instagram: "kaidabeautystudio",
  instagramUrl: "https://instagram.com/kaidabeautystudio",
};

export default function Footer() {
  const year = new Date().getFullYear();
  const { t } = useLang();

  return (
    <Wrap>
      <Inner>
        <Col $wide>
          <BrandName>Kaida Beauty Studio</BrandName>
          <Tagline>{t("footer.tagline")}</Tagline>

          <SwitcherRow>
            <LanguageSwitcher $dark />
          </SwitcherRow>
        </Col>

        <Col>
          <Heading>{t("footer.pages")}</Heading>
          <ul>
            <li>
              <FooterLink to="/">{t("nav.home")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/produktet">{t("nav.products")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/rreth-nesh">{t("nav.about")}</FooterLink>
            </li>
            <li>
              <FooterLink to="/kontakt">{t("nav.contact")}</FooterLink>
            </li>
          </ul>
        </Col>

        <Col>
          <Heading>{t("footer.contact")}</Heading>
          <ul>
            <li>
              <Ext href={`tel:${CONTACT.phoneHref}`}>
                <FiPhone /> {CONTACT.phone}
              </Ext>
            </li>
            <li>
              <Ext href={`mailto:${CONTACT.email}`}>
                <FiMail /> {CONTACT.email}
              </Ext>
            </li>
            <li>
              <Ext href={CONTACT.instagramUrl} target="_blank" rel="noreferrer">
                <FiInstagram /> {CONTACT.instagram}
              </Ext>
            </li>
            <li>
              <Plain>
                <FiMapPin /> {CONTACT.address}
              </Plain>
            </li>
          </ul>
        </Col>
      </Inner>

      <Bottom>
        <span>
          © {year} {CONTACT.company}. {t("footer.rights")}
        </span>
        <span>
          {t("footer.builtBy")}{" "}
          <Ext href="https://asitesagency.com" target="_blank" rel="noreferrer">
            ASites Agency
          </Ext>
        </span>
      </Bottom>
    </Wrap>
  );
}

/* ------------------------------------------------------------------ */

const Wrap = styled.footer`
  background: ${({ theme }) => theme.color.bgDark};
  color: rgba(255, 255, 255, 0.68);
  padding: 80px ${({ theme }) => theme.layout.gutter} 0;
`;

const Inner = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1.4fr;
  gap: 56px;
  padding-bottom: 56px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }
  ${({ theme }) => theme.down.sm} {
    grid-template-columns: 1fr;
  }
`;

const Col = styled.div`
  ${({ $wide, theme }) =>
    $wide &&
    `
    ${theme.down.lg} { grid-column: 1 / -1; }
  `}

  li {
    margin-bottom: 12px;
  }
`;

const BrandName = styled.p`
  margin: 0 0 14px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: 1.6rem;
  color: #fff;
`;

const SwitcherRow = styled.div`
  margin-top: 22px;
`;

const Tagline = styled.p`
  margin: 0;
  max-width: 42ch;
  font-size: ${({ theme }) => theme.size.sm};
  line-height: 1.7;
`;

const Heading = styled.h4`
  margin: 0 0 18px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
`;

const FooterLink = styled(Link)`
  font-size: ${({ theme }) => theme.size.sm};
  transition: color 0.25s;

  &:hover {
    color: #fff;
  }
`;

const Ext = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: ${({ theme }) => theme.size.sm};
  transition: color 0.25s;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.color.brand};
  }

  &:hover {
    color: #fff;
  }
`;

const Plain = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  font-size: ${({ theme }) => theme.size.sm};
  line-height: 1.6;

  svg {
    flex-shrink: 0;
    margin-top: 4px;
    color: ${({ theme }) => theme.color.brand};
  }
`;

const Bottom = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 22px 0 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: ${({ theme }) => theme.size.xs};
  color: rgba(255, 255, 255, 0.45);

  ${({ theme }) => theme.down.sm} {
    flex-direction: column;
  }
`;
